import { useRef, useState } from "react";
import { Box, Button, Drawer, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ChesslaBrand } from "./ChesslaBrand";
import { isMobileNavItemActive, isPrimaryNavigationDisabled, navItems } from "./navigation";
import { NavItemIcon } from "./NavItemIcon";
import { NikolaCreditsLink } from "@/features/entitlements/NikolaCreditsLink";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { useRememberedRoute } from "@/features/preferences/page-state";

export interface MobileTopMenuProps {
  pathname: string;
  onboardingCompleted: boolean;
  isAnalyzing: boolean;
  onNavigate?: () => void;
  onLogout: () => void;
}

export const MobileTopMenu = ({
  pathname,
  onboardingCompleted,
  isAnalyzing,
  onNavigate,
  onLogout,
}: MobileTopMenuProps) => {
  const [open, setOpen] = useState(false);
  const rememberedRoutes: Record<string, string> = {
    "/games": useRememberedRoute("/games"),
    "/patterns": useRememberedRoute("/patterns"),
    "/trends": useRememberedRoute("/trends"),
  };
  const availableCredits = useEntitlementsStore((state) => state.snapshot?.credits.available ?? null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const close = () => {
    setOpen(false);
    onNavigate?.();
  };
  return (
    <>
      <Button
        aria-label="Open navigation"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        disableRipple
        sx={{
          minWidth: 72,
          minHeight: 44,
          borderLeft: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
          px: 1.25,
          color: "inherit",
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: ".1em",
        }}
      >
        MENU{" "}
        <Box
          component="span"
          aria-hidden
          sx={{
            position: "relative",
            width: 9,
            height: 9,
            ml: 0.75,
            flexShrink: 0,
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              bgcolor: "currentColor",
            },
            "&::before": { inset: "4px 0 auto", height: 1 },
            "&::after": { inset: "0 auto 0 4px", width: 1 },
          }}
        />
      </Button>
      <Drawer
        anchor="top"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100dvh",
            maxHeight: "100dvh",
            overflowX: "clip",
            bgcolor: "background.default",
            backgroundImage: "none",
            transition: "none",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          },
        }}
        SlideProps={{ timeout: 0, onEntered: () => closeRef.current?.focus() }}
      >
        {/* On iPhones the screen's rounded corners and home indicator crop anything on the very edge, so the
            closing red line sits above the bottom safe area and content clears the notch in landscape. */}
        <Stack
          data-testid="mobile-menu-sheet"
          sx={{
            minHeight: "calc(100% - env(safe-area-inset-bottom))",
            mb: "env(safe-area-inset-bottom)",
            pt: "env(safe-area-inset-top)",
            pl: "env(safe-area-inset-left)",
            pr: "env(safe-area-inset-right)",
            borderBottom: "2px solid",
            borderColor: "secondary.main",
          }}
        >
          <Box
            sx={{
              minHeight: 64,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid",
              borderColor: "divider",
              color: "primary.main",
            }}
          >
            <ChesslaBrand />
            <Button
              ref={closeRef}
              aria-label="Close navigation"
              onClick={() => setOpen(false)}
              disableRipple
              sx={{
                minWidth: 88,
                minHeight: 52,
                justifyContent: "flex-end",
                borderRadius: 0,
                color: "inherit",
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: ".12em",
              }}
            >
              CLOSE{" "}
              <Box component="span" aria-hidden sx={{ ml: 1, fontSize: 20 }}>
                ×
              </Box>
            </Button>
          </Box>
          <Stack component="nav" aria-label="Primary navigation" spacing={0} sx={{ flex: 1 }}>
            {navItems.map((item, index) => {
              const active = isMobileNavItemActive(pathname, item.path);
              const disabled = isPrimaryNavigationDisabled(onboardingCompleted);
              return (
                <Box
                  key={item.path}
                  component={RouterLink}
                  to={rememberedRoutes[item.path] ?? item.path}
                  aria-current={active ? "page" : undefined}
                  aria-disabled={disabled || undefined}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    if (disabled) event.preventDefault();
                    else close();
                  }}
                  sx={{
                    position: "relative",
                    display: "grid",
                    gridTemplateColumns: "2.25rem minmax(0,1fr) auto",
                    alignItems: "center",
                    minHeight: 64,
                    px: 2,
                    color: active ? "background.default" : "text.primary",
                    bgcolor: active ? "primary.main" : "transparent",
                    opacity: disabled ? 0.45 : 1,
                    textDecoration: "none",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          inset: "8px auto 8px 0",
                          width: 4,
                          bgcolor: "secondary.main",
                        }
                      : undefined,
                  }}
                >
                  <Typography
                    aria-hidden
                    sx={{
                      color: active ? "inherit" : "text.secondary",
                      fontSize: 10,
                      fontWeight: 900,
                      letterSpacing: ".08em",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "clamp(1.15rem, 5vw, 1.45rem)",
                        lineHeight: 1,
                        fontWeight: 900,
                        letterSpacing: "-.035em",
                        textTransform: "uppercase",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: active ? "inherit" : "text.secondary",
                      }}
                    >
                      <NavItemIcon icon={item.icon} size={13} />
                      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: ".1em" }}>
                        {active ? "CURRENT WORKSPACE" : "OPEN WORKSPACE"}
                      </Typography>
                    </Box>
                  </Box>
                  {item.path === "/analyze" && isAnalyzing && (
                    <Typography variant="caption" sx={{ color: "secondary.main", fontWeight: 900 }}>
                      RUNNING
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
          <NikolaCreditsLink available={availableCredits} compact />
          <Button
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            disableRipple
            sx={{
              minHeight: 56,
              justifyContent: "flex-start",
              px: 2,
              borderTop: "2px solid",
              borderColor: "primary.main",
              borderRadius: 0,
              color: "secondary.main",
              fontSize: ".75rem",
              fontWeight: 900,
              letterSpacing: ".1em",
              "&:hover": { bgcolor: "secondary.main", color: "background.default" },
            }}
          >
            LOGOUT
          </Button>
          <Box sx={{ px: 2, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ color: "text.secondary", fontSize: 10, fontWeight: 900, letterSpacing: ".12em" }}>
              VERIFIED CHESS WORKSPACE · YOUR GAMES, ONE FORMAT AT A TIME
            </Typography>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
};
